import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, Pill, AlertTriangle, FileText, Activity as ActIcon,
  Clock, ShieldAlert, Target, Phone,
} from "lucide-react";
import api from "@/lib/api";
import { Avatar, PriorityBadge, scoreColor } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const TABS = ["Overview", "Timeline", "Medications", "Incidents"];
const ICONS = { pill: Pill, alert: AlertTriangle, file: FileText, activity: ActIcon };

export default function ResidentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [summary, setSummary] = useState("");
  const [sumLoading, setSumLoading] = useState(false);

  useEffect(() => {
    api.get(`/residents/${id}`).then((x) => setR(x.data)).catch(() => {});
    api.get(`/residents/${id}/timeline`).then((x) => setTimeline(x.data)).catch(() => {});
  }, [id]);

  const genSummary = async () => {
    setSumLoading(true);
    try {
      const { data } = await api.post(`/ai/resident-summary/${id}`);
      setSummary(data.summary);
    } catch (e) { setSummary("AI summary unavailable right now."); }
    finally { setSumLoading(false); }
  };

  if (!r) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  return (
    <div>
      <button onClick={() => navigate("/residents")} data-testid="back-to-residents"
        className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Residents
      </button>

      <div className="rounded-xl border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start gap-4">
          <Avatar name={`${r.first_name} ${r.last_name}`} seed={r.avatar_seed || 0} size={64} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{r.first_name} {r.last_name}</h1>
              <PriorityBadge level={r.risk_level} />
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {r.program_name} · Room {r.room} · DOB {r.dob}
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {(r.diagnoses || []).map((d) => <span key={d} className="rounded-full bg-muted px-2.5 py-0.5 font-medium">{d}</span>)}
              {(r.allergies || []).filter((a) => a !== "None known").map((a) => (
                <span key={a} className="rounded-full bg-[#F0443815] px-2.5 py-0.5 font-medium text-[#F04438]">Allergy: {a}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: scoreColor(100 - r.risk_score) }}>{r.risk_score}</div>
              <div className="overline">Risk</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: scoreColor(r.community_score) }}>{r.community_score}</div>
              <div className="overline">Community</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="mb-4 flex gap-1 rounded-lg border bg-card p-1">
            {TABS.map((t) => (
              <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                className={cn("flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  tab === t ? "bg-[#155EEF] text-white" : "text-muted-foreground hover:bg-muted")}>
                {t}
              </button>
            ))}
          </div>

          {tab === "Overview" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Panel title="ISP Goals" icon={Target}>
                <ul className="space-y-2 text-sm">
                  {(r.isp_goals || []).map((g, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#155EEF]" />{g}</li>
                  ))}
                </ul>
              </Panel>
              <Panel title="Care Team" icon={ShieldAlert}>
                <ul className="space-y-1.5 text-sm">{(r.care_team || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
              </Panel>
              <Panel title="Guardian / Family" icon={Phone}>
                <div className="text-sm">{r.guardian}</div>
                <div className="text-sm text-muted-foreground">{r.guardian_phone}</div>
              </Panel>
              <Panel title="Active Medications" icon={Pill}>
                <div className="text-sm text-muted-foreground">{(r.medications || []).length} active orders</div>
                <ul className="mt-1 space-y-1 text-sm">
                  {(r.medications || []).slice(0, 4).map((m) => <li key={m.id}>{m.name} {m.dose}</li>)}
                </ul>
              </Panel>
            </div>
          )}

          {tab === "Timeline" && (
            <div className="rounded-xl border bg-card p-5 shadow-sm">
              <div className="relative space-y-4 pl-6 before:absolute before:left-2 before:top-1 before:h-full before:w-px before:bg-border">
                {timeline.map((e, i) => {
                  const Icon = ICONS[e.icon] || Clock;
                  const col = { critical: "#F04438", warning: "#F79009", success: "#12B76A", normal: "#155EEF" }[e.severity];
                  return (
                    <div key={i} className="relative">
                      <span className="absolute -left-6 top-0.5 flex h-4 w-4 items-center justify-center rounded-full" style={{ backgroundColor: `${col}22`, color: col }}>
                        <Icon size={10} />
                      </span>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{e.type} · {String(e.ts).slice(0, 10)}</div>
                      <div className="text-sm font-medium">{e.title}</div>
                      <div className="text-xs text-muted-foreground">{e.detail}</div>
                    </div>
                  );
                })}
                {timeline.length === 0 && <div className="text-sm text-muted-foreground">No timeline events.</div>}
              </div>
            </div>
          )}

          {tab === "Medications" && (
            <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
              {(r.medications || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between border-b px-4 py-3 last:border-0">
                  <div><div className="text-sm font-medium">{m.name} {m.dose}</div>
                    <div className="text-xs text-muted-foreground">{m.route} · {m.schedule}</div></div>
                  {m.prn ? <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">PRN</span>
                    : <span className="text-xs text-muted-foreground">{m.refills_remaining} refills</span>}
                </div>
              ))}
            </div>
          )}

          {tab === "Incidents" && (
            <div className="space-y-3">
              {(r.incidents || []).map((inc) => (
                <div key={inc.id} className="rounded-xl border bg-card p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{inc.type}</span><PriorityBadge level={inc.severity} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{String(inc.occurred_at).slice(0, 10)} · reported by {inc.reported_by}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{inc.summary}</p>
                </div>
              ))}
              {(r.incidents || []).length === 0 && <div className="text-sm text-muted-foreground">No incidents recorded.</div>}
            </div>
          )}
        </div>

        {/* AI summary */}
        <div className="lg:col-span-4">
          <div className="rounded-xl border bg-card shadow-sm" style={{ borderLeft: "3px solid #7C3AED" }}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-bold"><Sparkles size={15} className="text-[#7C3AED]" /> AI Care Summary</div>
            </div>
            <div className="p-4">
              {summary ? (
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed">{summary}</p>
              ) : (
                <p className="text-sm text-muted-foreground">Generate an AI handoff summary of recent status, grounded in this resident's records. Observations only — not a diagnosis.</p>
              )}
              <button data-testid="generate-summary-btn" onClick={genSummary} disabled={sumLoading}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-[#7C3AED] py-2 text-sm font-semibold text-white transition-colors hover:bg-[#6D28D9] disabled:opacity-60">
                {sumLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {sumLoading ? "Generating…" : "Generate AI Summary"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold"><Icon size={15} className="text-[#155EEF]" /> {title}</div>
      {children}
    </div>
  );
}
